import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import UserModel from '../../core/models/user.model';
import { UserService } from '../../core/services/user.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';

/**
 * @component ForgotPasswordStep2Component
 * @description Component responsible for handling the second step of the password recovery process.
 * This component allows users to set a new password after verifying their identity in step 1.
 * It includes form validation for password strength and confirmation, and communicates with
 * the UserService to update the password in the backend.
 * 
 * @selector app-forgot-password-step2
 * @template ./forgot-password-step2.component.html
 * @styleUrl ./forgot-password-step2.component.scss
 */
@Component({
  selector: 'app-forgot-password-step2',
  templateUrl: './forgot-password-step2.component.html',
  styleUrls: ['./forgot-password-step2.component.scss']
})
export class ForgotPasswordStep2Component {
  /**
   * @property {number} number
   * @description User ID or identifier retrieved from route parameters, used to associate
   * the password update with the correct user account.
   */
  public number: number;

  /**
   * @property {FormGroup} updatePasswordForm
   * @description FormGroup instance that manages the password update form controls and
   * validation rules. Includes password and confirmpassword fields with validators.
   */
  public updatePasswordForm: FormGroup;

  /**
   * @property {boolean} submitted
   * @description Flag indicating whether the password update form has been submitted.
   * Used to control visibility of validation error messages in the template.
   */
  public submitted = false;

  /**
   * @property {UserModel} updatePassword
   * @description Model instance containing user password data (password and confirm_password)
   * that will be sent to the backend service for updating.
   */
  public updatePassword: UserModel;

  /**
   * @method constructor
   * @description Initializes the component with required Angular services and sets up the
   * password update form with validation rules.
   * 
   * @param {Router} router - Service for navigating between routes
   * @param {ActivatedRoute} route - Service for accessing route parameters
   * @param {UserService} userService - Service for user-related operations including password update
   * @param {FormBuilder} formBuilder - Service for building and managing FormGroup instances
   * @param {LocationStrategy} locationStrategy - Service for getting current route location
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous navigation route
   */
  constructor(public router: Router, public route: ActivatedRoute, public userService: UserService, public formBuilder: FormBuilder,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.updatePassword = new UserModel();
    /*validation*/
    this.updatePasswordForm = this.formBuilder.group({
      password: ['', [Validators.required,
      Validators.minLength(6),
      Validators.maxLength(10)]],
      confirmpassword: ['', [Validators.required]],
    });
    /*validation end*/
  }

  /**
   * @method ngOnInit
   * @description Angular lifecycle hook called when the component is initialized.
   * Extracts the user ID from route parameters and sets the previous route for navigation.
   * 
   * @async
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    this.route.params.subscribe(params => {
      this.number = params.id;
    });
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
  }

  /**
   * @method f
   * @description Getter method providing convenient access to the form controls of the
   * updatePasswordForm. Used in the template for form validation and error handling.
   * 
   * @returns {AbstractControl} Form controls object containing all form field controls
   */
  public get f() {
    return this.updatePasswordForm.controls;
  }

  /**
   * @method createPassword
   * @description Handles the password update process. Validates the form, checks if passwords match,
   * calls the UserService to update the password, and navigates to login page on success.
   * Displays alerts for validation errors and confirmation messages.
   * 
   * @async
   * @returns {Promise<boolean|void>} Returns false if passwords don't match, otherwise void
   * @throws {Error} Catches and alerts any errors from the service call
   */
  public async createPassword() {
    try {
      this.submitted = true;
      // stop here if form is invalid
      if (this.updatePasswordForm.invalid) {
        return;
      }
      if (this.updatePassword.password != this.updatePassword.confirm_password) {
        alert('Incorrect confirm password');
        return false;
      }
      this.updatePassword = await this.userService.updatePassword(this.updatePassword.password, this.number);
      if (this.updatePassword) {
        alert('Password was updated successfully.');
        this.router.navigate(['/login']);
      }
    } catch (error) {
      alert(error);
    }
  }
}
